import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState, FormEvent, ChangeEvent } from "react";
import goArrowSvg from "../../assets/go_arrow.svg";
import {
  buttonArrowSx,
  buttonSx,
  emailInputSx,
  formContainerSx,
  subtitleSx,
  titleSx,
} from "./EmailCaptureForm.sx";

enum FormStatus {
  Idle = "idle",
  Submitting = "submitting",
  Success = "success",
  Error = "error",
}

export interface EmailCaptureFormProps {}

export const EmailCaptureForm = ({}: EmailCaptureFormProps) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<FormStatus>(FormStatus.Idle);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(FormStatus.Submitting);

    const formData = new URLSearchParams();
    formData.append("form-name", "contact");
    formData.append("email", email);

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });

      if (response.ok) {
        setStatus(FormStatus.Success);
        setEmail("");
      } else {
        setStatus(FormStatus.Error);
      }
    } catch {
      setStatus(FormStatus.Error);
    }
  };

  const getSubtitleText = () => {
    switch (status) {
      case FormStatus.Success:
        return "Thank you! We'll be in touch soon.";
      case FormStatus.Error:
        return "Something went wrong. Please try again.";
      default:
        return "Leave us your email, and we do the rest.";
    }
  };

  const getButtonText = () => {
    return status === FormStatus.Submitting ? "SENDING..." : "GO";
  };

  const isFormDisabled = status === FormStatus.Submitting;

  return (
    <Stack
      alignItems="center"
      gap={{ xs: "2rem", md: "3rem" }}
      width="100%"
      sx={formContainerSx}
    >
      <Stack gap="1rem">
        <Typography sx={titleSx}>Interested?</Typography>
        <Typography sx={subtitleSx}>{getSubtitleText()}</Typography>
      </Stack>

      {status !== FormStatus.Success && (
        <Box
          component="form"
          name="contact"
          method="POST"
          data-netlify="true"
          netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          sx={{ width: "100%" }}
        >
          <input type="hidden" name="form-name" value="contact" />
          <Box sx={{ display: "none" }}>
            <input name="bot-field" />
          </Box>

          <Stack gap="3rem" width="100%">
            <TextField
              name="email"
              placeholder="E-mail"
              variant="standard"
              type="email"
              value={email}
              onChange={handleChange}
              required
              disabled={isFormDisabled}
              sx={emailInputSx}
            />
            <Button type="submit" sx={buttonSx} disabled={isFormDisabled}>
              {getButtonText()}
              <Box
                component="img"
                src={goArrowSvg}
                alt="arrow"
                sx={buttonArrowSx}
              />
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};

export default EmailCaptureForm;
